import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SheetState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.font.FontWeight.Companion.SemiBold
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.banko_rules_title
import banko.composeapp.generated.resources.how_to_play
import banko.composeapp.generated.resources.how_to_play_desc
import banko.composeapp.generated.resources.how_to_win
import banko.composeapp.generated.resources.how_to_win_desc
import banko.composeapp.generated.resources.mixed_play
import banko.composeapp.generated.resources.mixed_play_desc
import banko.composeapp.generated.resources.multiple_devices
import banko.composeapp.generated.resources.multiple_devices_desc
import banko.composeapp.generated.resources.players
import banko.composeapp.generated.resources.players_count
import banko.composeapp.generated.resources.rules
import banko.composeapp.generated.resources.settings
import banko.composeapp.generated.resources.single_device
import banko.composeapp.generated.resources.single_device_desc
import org.jetbrains.compose.resources.stringResource

@Composable
fun BankoStartScreen(
    bankoStartScreenViewModel: BankoStartScreenViewModel,
    isCurrentScreen: Boolean = true,
    onShowSnackBar: (String) -> Unit = {},
    navigateToGame: (String, String) -> Unit
) {

    val uiState = bankoStartScreenViewModel.bankoStartScreenUiState

    val errorMessage by uiState.errorMessageFlow.collectAsState()

    LaunchedEffect(errorMessage) {
        if (errorMessage != null) {
            errorMessage?.let {
                onShowSnackBar(it)
                uiState.errorMessageFlow.reset()
            }
        }
    }

    // Invite links wait until the player is back here (the current, settled screen) rather than
    // joining a second game behind the first.
    DisposableEffect(uiState, isCurrentScreen) {
        uiState.onScreenVisibilityChanged(isCurrentScreen)
        onDispose { uiState.onScreenVisibilityChanged(false) }
    }

    val joinedGameIsValid by uiState.joinedGameIsValidFlow.collectAsState()

    LaunchedEffect(joinedGameIsValid) {
        if (joinedGameIsValid == true) {
            navigateToGame(
                uiState.joinedGameCodeFlow.value,
                uiState.userNameFlow.value
            )
            uiState.joinedGameIsValidFlow.reset()
        }
    }

    BankoStartScreen(
        uiState = uiState,
    )
}


@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BankoStartScreen(
    uiState: BankoStartScreenUiState
) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val bottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    var showBottomSheet by remember { mutableStateOf(false) }
    var showSettings by remember { mutableStateOf(false) }
    var startSettingsOnDice by remember { mutableStateOf(false) }

    // Returning from a completed practice round: open the dice picker so the freshly earned
    // Training Wheels are the first thing the player sees, ready to equip.
    LaunchedEffect(Unit) {
        if (PracticeGameSession.consumeDiceShowcase()) {
            startSettingsOnDice = true
            showSettings = true
        }
    }
    val focusManager = LocalFocusManager.current
    val coroutineScope = rememberCoroutineScope()
    val showPublicGameBrowser by uiState.publicGameBrowserVisibleFlow.collectAsState()

    if (showBottomSheet) {
        RulesBottomSheet(bottomSheetState = bottomSheetState, onDismissRequest = {
            showBottomSheet = false
        })
    }

    if (showSettings) {
        BankoSettingsSheet(
            uiState = uiState,
            startOnDice = startSettingsOnDice,
            onDismiss = { showSettings = false },
        )
    }

    if (showPublicGameBrowser) {
        PublicGameBrowserSheet(uiState)
    }

    // Leaves / bats / pumpkins sit behind the form and run up behind the status bar, as on iOS.
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
    StartScreenSeasonalEffects(modifier = Modifier.fillMaxSize().extendIntoStatusBar())
    Scaffold(
        containerColor = Color.Transparent,
    ) { paddingValues ->
        BoxWithConstraints(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize()
        ) {
            val isLoading by uiState.isLoadingFlow.collectAsState()
            val userName by uiState.userNameFlow.collectAsState()
            val gameCode by uiState.gameCodeFlow.collectAsState()
            val isOfflineMode by uiState.isOfflineModeFlow.collectAsState()
            val isPhoneLandscape = isConstrainedPhoneLandscape(maxWidth, maxHeight)
            val isMobile = maxWidth < 600.dp
            val cardModifier = Modifier
                .align(Alignment.Center)
                .widthIn(max = 420.dp)
                .padding(horizontal = 16.dp)
            val cardColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.98f)
            val cardElevation = 12.dp
            val cardShape = RoundedCornerShape(24.dp)
            val cardPadding = PaddingValues(vertical = 40.dp, horizontal = 32.dp)

            val contentModifier = if (isMobile) {
                Modifier
                    .align(Alignment.TopCenter)
                    .fillMaxSize()
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
            } else {
                Modifier
                    .padding(cardPadding)
            }

            if (isPhoneLandscape) {
                BankoStartScreenContent(
                    // Modest top room only; the two panes center vertically, so the floating
                    // gear/Rules sit over empty corners and don't need to be padded around.
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(start = 18.dp, top = 16.dp, end = 18.dp, bottom = 10.dp)
                        .imePadding(),
                    isMobile = true,
                    isPhoneLandscape = true,
                    userName = userName,
                    gameCode = gameCode,
                    isOfflineMode = isOfflineMode,
                    isLoading = isLoading,
                    onOpenDice = {
                        startSettingsOnDice = true
                        showSettings = true
                    },
                    focusManager = focusManager,
                    keyboardController = keyboardController,
                    coroutineScope = coroutineScope,
                    uiState = uiState,
                )
            } else if (isMobile) {
                val scrollState = androidx.compose.foundation.rememberScrollState()
                val mobileTopPadding = if (maxHeight > 760.dp) {
                    (56.dp + (maxHeight - 760.dp) * 0.25f).coerceAtMost(88.dp)
                } else {
                    56.dp
                }
                Column(
                    modifier = contentModifier
                        .verticalScroll(scrollState)
                        .imePadding()
                        .padding(top = mobileTopPadding, bottom = 24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    BankoStartScreenContent(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp),
                        isMobile = true,
                        userName = userName,
                        gameCode = gameCode,
                        isOfflineMode = isOfflineMode,
                        isLoading = isLoading,
                        onOpenDice = {
                            startSettingsOnDice = true
                            showSettings = true
                        },
                        focusManager = focusManager,
                        keyboardController = keyboardController,
                        coroutineScope = coroutineScope,
                        uiState = uiState
                    )
                }
            } else {
                val scrollState = androidx.compose.foundation.rememberScrollState()
                Surface(
                    // Cap the card to the available height so its content scrolls internally instead of
                    // overflowing the screen — otherwise on short/landscape windows the lower rows
                    // (dice entry, remove-ads, privacy policy) get clipped off the bottom.
                    modifier = cardModifier.heightIn(max = maxHeight - 32.dp),
                    color = cardColor,
                    tonalElevation = cardElevation,
                    shape = cardShape,
                    shadowElevation = cardElevation
                ) {
                    Column(
                        modifier = Modifier
                            .verticalScroll(scrollState)
                            .padding(cardPadding)
                            .imePadding(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        BankoStartScreenContent(
                            modifier = Modifier.fillMaxWidth(),
                            isMobile = false,
                            userName = userName,
                            gameCode = gameCode,
                            isOfflineMode = isOfflineMode,
                            isLoading = isLoading,
                            onOpenDice = {
                                startSettingsOnDice = true
                                showSettings = true
                            },
                            focusManager = focusManager,
                            keyboardController = keyboardController,
                            coroutineScope = coroutineScope,
                            uiState = uiState
                        )
                    }
                }
            }

            // Settings (gear) floats top-left, mirroring the top-right Rules affordance. It opens a
            // sheet holding all the secondary controls (preferences, dice, store, privacy) that used
            // to crowd the main screen.
            BankoChromeIconButton(
                icon = BankoIcons.Settings,
                contentDescription = stringResource(Res.string.settings),
                onClick = {
                    startSettingsOnDice = false
                    showSettings = true
                },
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(12.dp),
            )

            // "Rules" floats top-right over the content instead of living in a fixed-color app bar
            // that would clip the scrolling content behind an invisible (same-as-background) edge.
            BankoChromePillButton(
                text = stringResource(Res.string.rules),
                onClick = { showBottomSheet = true },
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(12.dp),
            )
        }
    }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RulesBottomSheet(
    bottomSheetState: SheetState,
    onDismissRequest: () -> Unit = {},
    eventRules: Pair<String, String>? = null,
) {
    // statusBarsPadding caps the sheet (Surface + drag handle) below the status bar so it can't bleed
    // under it; contentWindowInsets(0) avoids double top-padding, and the content adds its own
    // navigationBarsPadding so the last rules clear the nav bar. The sheet still reaches the screen
    // bottom, so the scrim covers the nav bar.
    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding(),
        onDismissRequest = onDismissRequest,
        sheetState = bottomSheetState,
        contentWindowInsets = { WindowInsets(0) },
    ) {
        // Section = (heading, body). Rendered as raised rimmed cards, matching the settings cards
        // and the rest of the app — and inheriting the app's Fredoka font instead of forcing the
        // system default (which made this screen look like a different app).
        val sections = listOfNotNull(eventRules) + listOf(
            stringResource(Res.string.players) to stringResource(Res.string.players_count),
            stringResource(Res.string.how_to_play) to stringResource(Res.string.how_to_play_desc),
            stringResource(Res.string.how_to_win) to stringResource(Res.string.how_to_win_desc),
            stringResource(Res.string.single_device) to stringResource(Res.string.single_device_desc),
            stringResource(Res.string.multiple_devices) to stringResource(Res.string.multiple_devices_desc),
            stringResource(Res.string.mixed_play) to stringResource(Res.string.mixed_play_desc),
        )

        LazyColumn(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .navigationBarsPadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            item {
                Text(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                    text = stringResource(Res.string.banko_rules_title),
                    fontFamily = theme.bankoDisplayFontFamily(),
                    fontSize = 40.sp,
                    color = MaterialTheme.colorScheme.primary,
                    textAlign = TextAlign.Center,
                )
            }

            items(sections) { (heading, body) ->
                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = MaterialTheme.colorScheme.surfaceContainerHigh,
                    shadowElevation = 2.dp,
                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp)) {
                        Text(
                            text = heading,
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = SemiBold,
                            color = MaterialTheme.colorScheme.onSurface,
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            text = body,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }
        }
    }
}
